"use client";

import { CaretRight, EnvelopeSimple } from "@repo/ui/components/icons";
import { buttonVariants } from "@repo/ui/components/ui/button";
import { fieldClass, fieldFrame } from "@repo/ui/components/ui/input";
import { cn } from "@repo/ui/lib/utils";
import Link from "next/link";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { clearPageCaches } from "../../components/pwa/caches";
import { getCaptcha, loginWithEmail, requestOtp, verifyMfa, verifyOtp, type CaptchaAnswer, type Me, type SignInResult } from "../../../lib/api-client";

// Sign in or sign up with a mobile number (one flow: the first verified code
// creates the account, unverified until Shahkar — F13). Phone → 6-digit
// code (auto-filled from the SMS where the platform allows: WebOTP on
// Android, the keyboard suggestion on iOS) → done. Email + password stays as
// a secondary way in. Used full-page on /signin and embedded in /checkout's
// first step (F110).

type Step = "phone" | "code" | "email" | "mfa";
const CODE_LENGTH = 6;
const PERSIAN = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC = "٠١٢٣٤٥٦٧٨٩";
const latin = (s: string) => s.replace(/[۰-۹٠-٩]/g, (d) => String(PERSIAN.includes(d) ? PERSIAN.indexOf(d) : ARABIC.indexOf(d)));
const fa = (s: string | number) => String(s).replace(/\d/g, (d) => PERSIAN[Number(d)]!);

/** The national 11-digit form (09121234567) of what was typed, or null if it isn't an Iranian mobile yet. */
function nationalMobile(input: string): string | null {
  const m = /^(?:\+98|0098|98|0)?(9\d{9})$/.exec(latin(input).replace(/[\s\-()]/g, ""));
  return m ? `0${m[1]}` : null;
}

/** 0912 345 6789 while typing (digits only, max 11). */
function groupPhone(raw: string): string {
  const d = latin(raw).replace(/\D/g, "").slice(0, 11);
  return [d.slice(0, 4), d.slice(4, 7), d.slice(7)].filter(Boolean).join(" ");
}

// The shared main button and field (F123), at sign-in's larger phone-first size.
// Blocked by staff (F186); the api answers it only after the code or password was right.
const BLOCKED = "حساب شما مسدود شده است. با پشتیبانی تماس بگیرید.";
const isBlocked = (data: unknown) => !!data && typeof data === "object" && "error" in data && data.error === "account_blocked";

const primaryBtn = cn(buttonVariants({ size: "lg" }), "w-full text-base");

function OtpBoxes({ value, onChange, disabled, invalid }: { value: string; onChange: (v: string) => void; disabled?: boolean; invalid?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => inputRef.current?.focus(), []);
  return (
    // One real input under six painted boxes: paste, SMS auto-fill and
    // backspace all just work, and screen readers see a single field.
    <div className="relative" dir="ltr" onClick={() => inputRef.current?.focus()}>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(latin(e.target.value).replace(/\D/g, "").slice(0, CODE_LENGTH))}
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label="کد تایید ۶ رقمی"
        maxLength={CODE_LENGTH}
        disabled={disabled}
        className="absolute inset-0 h-full w-full cursor-text opacity-0"
      />
      <div className="grid grid-cols-6 gap-2" aria-hidden>
        {Array.from({ length: CODE_LENGTH }, (_, i) => {
          const char = value[i];
          const active = i === Math.min(value.length, CODE_LENGTH - 1) && !disabled;
          return (
            <span
              key={i}
              className={cn(
                "bg-background flex aspect-[4/5] items-center justify-center rounded-2xl border text-2xl font-bold tabular-nums transition-colors",
                invalid ? "border-ink-red text-ink-red" : active ? "border-brand-strong ring-brand/30 ring-4" : char ? "border-foreground/30" : "border-border",
              )}
            >
              {char ? fa(char) : active ? <span className="bg-foreground h-6 w-0.5 animate-pulse rounded" /> : null}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** Shown only after repeated failures (F113): a self-hosted image CAPTCHA. */
function CaptchaBox({ svg, value, onChange, onRefresh }: { svg: string; value: string; onChange: (v: string) => void; onRefresh: () => void }) {
  return (
    <div className="bg-surface mt-4 flex flex-col gap-2 rounded-2xl p-3">
      <span className="text-muted-foreground text-xs">برای ادامه، عددهای تصویر را وارد کنید.</span>
      <div className="flex items-center gap-2">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`} alt="کد امنیتی" className="bg-background h-14 rounded-xl" />
        <button type="button" onClick={onRefresh} className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-4">
          تصویر دیگر
        </button>
      </div>
      <input
        inputMode="numeric"
        dir="ltr"
        value={fa(value)}
        onChange={(e) => onChange(latin(e.target.value).replace(/\D/g, "").slice(0, 6))}
        aria-label="کد امنیتی تصویر"
        className={cn(fieldClass, "h-12 text-lg font-semibold tracking-widest")}
      />
    </div>
  );
}

export function LoginScreen({
  onLoggedIn,
  embedded = false,
}: {
  onLoggedIn: (user: Me, info: { isNewUser: boolean }) => void;
  embedded?: boolean;
}) {
  const [step, setStep] = useState<Step>("phone");
  // False until this page's JavaScript runs (F203): on a weak first visit the
  // form shows long before it works, so the button says it's getting ready.
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [captcha, setCaptcha] = useState<{ id: string; svg: string } | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const submitted = useRef<string | null>(null);

  const national = nationalMobile(phone);
  const secondsLeft = resendAt ? Math.max(0, Math.ceil((resendAt - now) / 1000)) : 0;

  const loadCaptcha = useCallback(async () => {
    setCaptchaAnswer("");
    const { data } = await getCaptcha().catch(() => ({ data: null }));
    if (data) setCaptcha({ id: data.captchaId, svg: data.svg });
  }, []);

  /** The CAPTCHA answer to send (each CAPTCHA works once, so it's cleared after use). */
  const takeCaptcha = (): CaptchaAnswer => {
    if (!captcha) return {};
    const answer = { captchaId: captcha.id, captchaAnswer };
    setCaptcha(null);
    return answer;
  };

  /** True if the server wants a CAPTCHA (it then shows); the caller stops there. */
  const needsCaptcha = (data: unknown) => {
    const err = data && typeof data === "object" && "error" in data ? (data as { error: string }).error : null;
    if (err !== "captcha_required" && err !== "captcha_invalid") return false;
    setError(err === "captcha_invalid" ? "کد امنیتی درست نبود؛ دوباره تلاش کنید." : null);
    void loadCaptcha();
    return true;
  };

  /** The end of a successful first factor: signed in, or the 2FA step. */
  const finish = (data: SignInResult | null): boolean => {
    if (!data || !("ok" in data)) return false;
    if (data.mfaRequired) {
      setMfaToken(data.mfaToken);
      setMfaCode("");
      setStep("mfa");
      return true;
    }
    clearPageCaches();
    onLoggedIn(data.user, { isNewUser: data.isNewUser });
    return true;
  };

  useEffect(() => {
    if (!resendAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [resendAt]);

  async function sendCode() {
    if (!national) return;
    setLoading(true);
    setError(null);
    const { status, data } = await requestOtp(national, takeCaptcha()).catch(() => ({ status: 0, data: null }));
    setLoading(false);
    if (needsCaptcha(data)) return;
    if (data && "ok" in data) {
      setCode("");
      submitted.current = null;
      setResendAt(Date.now() + data.resendInSeconds * 1000);
      setStep("code");
      return;
    }
    const err = data && "error" in data ? (data as { error: string; retryAfterSeconds?: number }) : null;
    if (err?.error === "cooldown") {
      // A code is already on its way; go to the code step with the remaining wait.
      setResendAt(Date.now() + (err.retryAfterSeconds ?? 60) * 1000);
      setStep("code");
      return;
    }
    setError(
      err?.error === "invalid_phone"
        ? "شماره موبایل معتبر نیست."
        : err?.error === "rate_limited"
          ? "تعداد درخواست‌ها زیاد بود؛ کمی بعد دوباره تلاش کنید."
          : status === 0
            ? "اتصال برقرار نشد؛ اینترنت خود را بررسی کنید."
            : "ارسال کد انجام نشد؛ دوباره تلاش کنید.",
    );
  }

  const checkCode = useCallback(
    async (value: string) => {
      if (!national || value.length !== CODE_LENGTH || submitted.current === value) return;
      submitted.current = value;
      setLoading(true);
      setError(null);
      const { data } = await verifyOtp(national, value, takeCaptcha()).catch(() => ({ data: null }));
      setLoading(false);
      if (needsCaptcha(data)) {
        submitted.current = null;
        return;
      }
      if (finish(data)) return;
      if (isBlocked(data)) return setError(BLOCKED);
      setError(data && "error" in data && data.error === "too_many_attempts" ? "تلاش‌های ناموفق زیاد بود؛ کد جدید بگیرید." : "کد وارد‌شده درست نیست یا منقضی شده است.");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [national, onLoggedIn, captcha, captchaAnswer],
  );

  // Submit as soon as the sixth digit lands (typed, pasted or auto-filled).
  useEffect(() => {
    if (step === "code" && code.length === CODE_LENGTH && !captcha) void checkCode(code);
  }, [code, step, checkCode, captcha]);

  // Android Chrome: read the code straight from the SMS (WebOTP; the SMS ends with "@host #code").
  useEffect(() => {
    if (step !== "code" || typeof window === "undefined" || !("OTPCredential" in window)) return;
    const ac = new AbortController();
    (navigator.credentials.get({ otp: { transport: ["sms"] }, signal: ac.signal } as CredentialRequestOptions) as Promise<{ code?: string } | null>)
      .then((otp) => otp?.code && setCode(otp.code.slice(0, CODE_LENGTH)))
      .catch(() => undefined);
    return () => ac.abort();
  }, [step]);

  async function emailLogin() {
    setLoading(true);
    setError(null);
    const { data } = await loginWithEmail(email, password, takeCaptcha()).catch(() => ({ data: null }));
    setLoading(false);
    if (needsCaptcha(data)) return;
    if (isBlocked(data)) return setError(BLOCKED);
    if (!finish(data)) setError("ایمیل یا رمز عبور درست نیست، یا ایمیل هنوز تایید نشده است.");
  }

  async function submitMfa(value: string) {
    if (!mfaToken) return;
    setLoading(true);
    setError(null);
    const { data } = await verifyMfa(mfaToken, value).catch(() => ({ data: null }));
    setLoading(false);
    if (finish(data)) return;
    if (isBlocked(data)) {
      setError(BLOCKED);
      setStep("phone");
      setMfaToken(null);
      return;
    }
    const err = data && "error" in data ? data.error : null;
    if (err === "mfa_expired" || err === "too_many_attempts") {
      setError(err === "mfa_expired" ? "زمان ورود تمام شد؛ دوباره وارد شوید." : "تلاش‌های ناموفق زیاد بود؛ دوباره وارد شوید.");
      setStep("phone");
      setMfaToken(null);
      return;
    }
    setError(useRecovery ? "کد بازیابی درست نیست یا قبلا استفاده شده است." : "کد برنامه درست نیست.");
  }

  const heading = (title: string, sub: React.ReactNode) =>
    embedded ? (
      <p className="text-muted-foreground mb-4 text-sm leading-6">{sub}</p>
    ) : (
      <div className="mb-8">
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-display">{title}</h1>
        <p className="text-muted-foreground mt-2 text-sm leading-6">{sub}</p>
      </div>
    );

  const errorLine = (
    <>
      {error ? (
        <p role="alert" className="text-ink-red mt-3 text-sm">
          {error}
        </p>
      ) : null}
      {captcha ? <CaptchaBox svg={captcha.svg} value={captchaAnswer} onChange={setCaptchaAnswer} onRefresh={() => void loadCaptcha()} /> : null}
    </>
  );

  // Page variant: actions sit at the bottom on phones (thumb reach), inline on larger screens.
  const actions = (children: React.ReactNode) => (
    <div className={cn("flex flex-col gap-3", embedded ? "mt-4" : "mt-auto pt-6 sm:mt-8 sm:pt-0")}>{children}</div>
  );

  // Phones: fill the screen so the main action sits at the bottom. Larger screens: natural height, centred by the page.
  const root = cn("flex flex-1 flex-col", !embedded && "sm:flex-none");

  if (step === "mfa") {
    return (
      <div className={root}>
        {heading(
          "تایید دومرحله‌ای",
          useRecovery ? "یکی از کدهای بازیابی خود را وارد کنید؛ هر کد فقط یک‌بار کار می‌کند." : "کد ۶ رقمی برنامه احراز هویت (مثل Google Authenticator) را وارد کنید.",
        )}
        {useRecovery ? (
          <input
            dir="ltr"
            autoFocus
            value={mfaCode}
            onChange={(e) => (setMfaCode(e.target.value.toLowerCase().slice(0, 9)), setError(null))}
            placeholder="abcd-efgh"
            aria-label="کد بازیابی"
            className={cn(fieldClass, "h-14 text-center text-xl font-semibold tracking-widest")}
          />
        ) : (
          <OtpBoxes
            value={mfaCode}
            onChange={(v) => {
              setMfaCode(v);
              setError(null);
              if (v.length === CODE_LENGTH) void submitMfa(v);
            }}
            disabled={loading}
            invalid={Boolean(error)}
          />
        )}
        {errorLine}
        <button
          type="button"
          onClick={() => (setUseRecovery((v) => !v), setMfaCode(""), setError(null))}
          className="text-muted-foreground hover:text-foreground mt-5 text-center text-sm underline underline-offset-4"
        >
          {useRecovery ? "استفاده از برنامه احراز هویت" : "دسترسی به برنامه ندارم؛ کد بازیابی"}
        </button>
        {actions(
          <button
            type="button"
            disabled={loading || (useRecovery ? mfaCode.replace("-", "").length !== 8 : mfaCode.length !== CODE_LENGTH)}
            onClick={() => void submitMfa(mfaCode)}
            className={primaryBtn}
          >
            {loading ? "در حال بررسی…" : "تایید و ورود"}
          </button>,
        )}
      </div>
    );
  }

  if (step === "email") {
    return (
      <div className={root}>
        {heading("ورود با ایمیل", "اگر برای حساب خود ایمیل و رمز عبور تعیین کرده‌اید، از این راه هم می‌توانید وارد شوید.")}
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-muted-foreground">ایمیل</span>
            <input
              type="email"
              dir="ltr"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={cn(fieldClass, "h-12 text-base")}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-muted-foreground">رمز عبور</span>
            <input
              type="password"
              dir="ltr"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && email && password && void emailLogin()}
              className={cn(fieldClass, "h-12 text-base")}
            />
          </label>
        </div>
        {errorLine}
        {actions(
          <>
            <button type="button" disabled={loading || !email || !password || (captcha !== null && captchaAnswer.length < 4)} onClick={() => void emailLogin()} className={primaryBtn}>
              {loading ? "در حال ورود…" : "ورود"}
            </button>
            <button type="button" onClick={() => (setStep("phone"), setError(null))} className="text-muted-foreground hover:text-foreground text-sm">
              ورود با شماره موبایل
            </button>
          </>,
        )}
      </div>
    );
  }

  if (step === "code") {
    return (
      <div className={root}>
        {heading(
          "کد تایید را وارد کنید",
          <>
            کد {fa(CODE_LENGTH)} رقمی به <bdi dir="ltr">{fa(groupPhone(national ?? phone))}</bdi> پیامک شد.{" "}
            <button type="button" onClick={() => (setStep("phone"), setError(null), setCode(""))} className="text-foreground font-medium underline underline-offset-4">
              ویرایش شماره
            </button>
          </>,
        )}
        <OtpBoxes value={code} onChange={(v) => (setCode(v), setError(null))} disabled={loading} invalid={Boolean(error)} />
        {errorLine}
        <div className="mt-5 text-center text-sm">
          {secondsLeft > 0 ? (
            <span className="text-muted-foreground tabular-nums">
              ارسال دوباره کد تا {fa(`${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`)}
            </span>
          ) : (
            <button type="button" disabled={loading} onClick={() => void sendCode()} className="text-foreground font-semibold underline underline-offset-4">
              ارسال دوباره کد
            </button>
          )}
        </div>
        {actions(
          <button
            type="button"
            disabled={loading || code.length !== CODE_LENGTH || (captcha !== null && captchaAnswer.length < 4)}
            onClick={() => ((submitted.current = null), void checkCode(code))}
            className={primaryBtn}
          >
            {loading ? "در حال بررسی…" : "تایید و ورود"}
          </button>,
        )}
      </div>
    );
  }

  return (
    <div className={root}>
      {heading("ورود | ثبت نام", "اگر حساب کاربری ندارید، ابتدا با شماره موبایل خود وارد شوید")}
      <label className="flex flex-col gap-2 text-sm">
        <span className="text-muted-foreground">شماره موبایل</span>
        <span
          className={cn(
            fieldFrame,
            "focus-within:border-brand-strong focus-within:ring-brand/30 flex h-14 items-center gap-3 px-4 focus-within:ring-4",
            error && "border-ink-red",
          )}
          dir="ltr"
        >
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            autoFocus={!embedded}
            value={fa(groupPhone(phone))}
            onChange={(e) => (setPhone(e.target.value), setError(null))}
            onKeyDown={(e) => e.key === "Enter" && national && void sendCode()}
            placeholder="۰۹۱۲ ۳۴۵ ۶۷۸۹"
            aria-invalid={Boolean(error)}
            className="placeholder:text-muted-foreground h-full w-full bg-transparent text-xl font-semibold tracking-wider outline-none"
          />
        </span>
      </label>
      {errorLine}
      {actions(
        <>
          <button type="button" disabled={loading || !national || (captcha !== null && captchaAnswer.length < 4)} onClick={() => void sendCode()} className={primaryBtn}>
            {loading ? (
              "در حال ارسال کد…"
            ) : ready ? (
              "دریافت کد تایید"
            ) : (
              // Before the JavaScript: the usual label, swapped by CSS after 1 s
              // (.signin-wait-*), so a quick load never shows the other one.
              <span className="grid">
                <span className="signin-wait-idle [grid-area:1/1]">دریافت کد تایید</span>
                <span className="signin-wait-busy [grid-area:1/1]">در حال آماده‌سازی…</span>
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => (setStep("email"), setError(null))}
            className="text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5 text-sm"
          >
            <EnvelopeSimple size={16} />
            ورود با ایمیل و رمز عبور
          </button>
          {embedded ? null : (
            <p className="text-muted-foreground text-center text-xs leading-5">
              ورود شما به معنای پذیرش{" "}
              <Link href="/tos" className="text-foreground underline underline-offset-4">
                قوانین و مقررات
              </Link>{" "}
              شمشاپ است.
            </p>
          )}
        </>,
      )}
    </div>
  );
}

/** App-style back button for the full-page variant. */
export function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="بازگشت"
      className="bg-background hover:bg-muted flex h-10 w-10 items-center justify-center rounded-full shadow-sm transition-colors"
    >
      <CaretRight size={18} />
    </button>
  );
}
